@if (!isLoaded()) {
    <div class="loading center-loader">
        <div class="spinner-wrapper">
            <mat-spinner [diameter]="50" />
        </div>
    </div>
}

<igx-grid
    #grid
    id="paged-grid-{{ lCounter }}"
    slGridState="{{ config().name }}"
    [preventSave]="shouldPreventSettingsSave"
    [data]="data()"
    width="100%"
    [rowHeight]="45"
    [allowFiltering]="true"
    [rowSelection]="canSelect ? 'multiple' : 'none'"
    (dataPreLoad)="processData()"
    (sortingDone)="processSorting()"
    (filteringDone)="processData()"
    (doubleClick)="doubleClick($event)"
    [primaryKey]="'id'"
    [class.loading]="true"
    [moving]="true"
    class="mt-4">
    <igx-paginator [perPage]="pageSize()">
        <igx-paginator-content>
            <div class="table-footer-paging d-flex">
                <div class="d-flex align-items-center justify-content-start">
                    <div class="page-size-wrapper">
                        <mat-form-field subscriptSizing="dynamic">
                            <mat-select [(value)]="pageSize" (selectionChange)="onPageSizeChange($event)">
                                @for (val of selectOptions; track val) {
                                    <mat-option [value]="val">
                                        {{ val }}
                                    </mat-option>
                                }
                            </mat-select>
                        </mat-form-field>
                    </div>
                </div>
                <div class="d-flex align-items-center justify-content-end">
                    <button [disabled]="isFirstPage" (click)="paginate(1, false)" igxIconButton="flat" igxRipple [igxRippleCentered]="true">
                        <igx-icon family="material">first_page</igx-icon>
                    </button>
                    <button [disabled]="isFirstPage" (click)="previousPage()" igxIconButton="flat" igxRipple [igxRippleCentered]="true">
                        <igx-icon family="material">chevron_left</igx-icon>
                    </button>
                    <span>{{ currentPage }} of {{ totalPages }}</span>
                    <button [disabled]="isLastPage" (click)="nextPage()" igxRipple [igxRippleCentered]="true" igxIconButton="flat">
                        <igx-icon family="material">chevron_right</igx-icon>
                    </button>
                    <button
                        [disabled]="isLastPage"
                        (click)="paginate(totalPages, false)"
                        igxIconButton="flat"
                        igxRipple
                        [igxRippleCentered]="true">
                        <igx-icon family="material">last_page</igx-icon>
                    </button>
                </div>
            </div>
        </igx-paginator-content>
    </igx-paginator>
    <igx-grid-toolbar class="pb-4">
        <igx-grid-toolbar-title>
            <div class="w-100 h-100 flex align-items-center">
                <mat-form-field floatLabel="auto" subscriptSizing="dynamic" class="w-100">
                    <input
                        matInput
                        type="search"
                        value=""
                        placeholder="{{ 'General.Search' | translate }}..."
                        (blur)="onSearchEnter($event)"
                        (keyup.enter)="onSearchEnter($event)" />
                    <button matSuffix mat-icon-button>
                        <mat-icon>search</mat-icon>
                    </button>
                </mat-form-field>
            </div>
        </igx-grid-toolbar-title>
        <igx-grid-toolbar-actions class="right-icons">
            @if (canChangeColumnVisibility) {
                <igx-grid-toolbar-hiding />
            }

            <igx-grid-toolbar-pinning />

            @if (canSaveRoleSettings) {
                <button
                    igxButton="outlined"
                    [style.height.px]="40"
                    [igxToggleAction]="dropdown"
                    [igxDropDownItemNavigation]="dropdown"
                    [overlaySettings]="dropdownOverlaySettings">
                    <igx-icon>settings</igx-icon>
                </button>
            }

            <igx-drop-down #dropdown>
                @if (canSaveRoleSettings) {
                    @for (role of roles; track role.id) {
                        <igx-drop-down-item>
                            <span>{{ role.name }}</span>
                            <span class="ml-5 float-right">
                                <button
                                    mat-icon-button
                                    #target="tooltipTarget"
                                    [igxTooltipTarget]="tooltipRefLoad"
                                    (click)="loadRoleSettings(+role.id)">
                                    <mat-icon>cloud_download</mat-icon>
                                </button>
                                <span #tooltipRefLoad="tooltip" igxTooltip>{{ 'General.Load' | translate }}</span>
                                <button
                                    mat-icon-button
                                    #target="tooltipTarget"
                                    [igxTooltipTarget]="tooltipRefSaveAs"
                                    (click)="saveRoleSettings(+role.id)">
                                    <mat-icon>cloud_upload</mat-icon>
                                </button>
                                <span #tooltipRefSaveAs="tooltip" igxTooltip>{{ 'General.Save' | translate }}</span>
                            </span>
                        </igx-drop-down-item>
                    }
                }
            </igx-drop-down>

            <button
                #target="tooltipTarget"
                [style.height.px]="40"
                [igxTooltipTarget]="tooltipRef"
                igxButton="outlined"
                (click)="resetRoleSettings()"
                class="ml-0">
                <igx-icon>settings_backup_restore</igx-icon>
            </button>
            <span #tooltipRef="tooltip" igxTooltip>{{ 'General.Reset' | translate }}</span>
        </igx-grid-toolbar-actions>
    </igx-grid-toolbar>
    @for (column of columns(); track column.fieldName) {
        @if (column.isCustomTemplate) {
            <igx-column field="{{ column.fieldName }}" [sortable]="column.isSortable" [header]="column.header">
                <ng-template igxCell let-cell="cell">
                    @if (getTemplateRef(column.fieldName); as template) {
                        <ng-container [ngTemplateOutlet]="template" [ngTemplateOutletContext]="{ value: cell.value }" />
                    }
                </ng-template>
            </igx-column>
        } @else {
            @switch (column.dataType) {
                @case (DataType.String) {
                    <igx-column
                        field="{{ column.fieldName }}"
                        [filterable]="column.allowFiltering"
                        [sortable]="column.isSortable"
                        [header]="column.header"
                        [dataType]="column.dataType"
                        [resizable]="column.isResizable"
                        [disableHiding]="!column.canHide"
                        [pinned]="column.isPinned"
                        [hidden]="column.isHidden"
                        [width]="column.width!"
                        [formatter]="column.formatter" />
                }
                @case (DataType.Number) {
                    @if (column.isTrendingValue) {
                        <igx-column
                            field="{{ column.fieldName }}"
                            [filterable]="column.allowFiltering"
                            [sortable]="column.isSortable"
                            [header]="column.header"
                            [dataType]="column.dataType"
                            [resizable]="column.isResizable"
                            [disableHiding]="!column.canHide"
                            [pinned]="column.isPinned"
                            [hidden]="column.isHidden"
                            [width]="column.width!">
                            <ng-template igxCell let-val>
                                <div class="currency-badge-container">
                                    @if (!!column.trendingThreshold) {
                                        @if (val > column.trendingThreshold) {
                                            <igx-icon class="float-left text-success">arrow_upward</igx-icon>
                                        } @else if (val < column.trendingThreshold) {
                                            <igx-icon class="float-left text-danger">arrow_downward</igx-icon>
                                        }
                                    }

                                    <!-- eslint-disable-next-line @angular-eslint/template/eqeqeq -->
                                    @if (val == column.trendingThreshold) {
                                        <igx-icon class="float-left">remove</igx-icon>
                                    }

                                    @if (getAsNumberColumn(column).displayValue) {
                                        <span
                                            [class.up-value]="column.trendingThreshold && val > column.trendingThreshold"
                                            [class.down-value]="column.trendingThreshold && val < column.trendingThreshold">
                                            {{ val | slNumber }}
                                        </span>
                                    }
                                </div>
                            </ng-template>
                        </igx-column>
                    } @else {
                        @switch (getAsNumberColumn(column).numberType) {
                            @case (NumberType.Currency) {
                                <igx-column
                                    field="{{ column.fieldName }}"
                                    [filterable]="column.allowFiltering"
                                    [sortable]="column.isSortable"
                                    [header]="column.header"
                                    [dataType]="column.dataType"
                                    [resizable]="column.isResizable"
                                    [disableHiding]="!column.canHide"
                                    [pinned]="column.isPinned"
                                    [hidden]="column.isHidden"
                                    [width]="column.width!"
                                    [formatter]="column.formatter">
                                    <ng-template igxCell let-cell="cell">CHF {{ cell.value | slNumber: 2 }}</ng-template>
                                </igx-column>
                            }
                            @case (NumberType.Percentage) {
                                <igx-column
                                    field="{{ column.fieldName }}"
                                    [filterable]="column.allowFiltering"
                                    [sortable]="column.isSortable"
                                    [header]="column.header"
                                    [dataType]="column.dataType"
                                    [resizable]="column.isResizable"
                                    [disableHiding]="!column.canHide"
                                    [pinned]="column.isPinned"
                                    [hidden]="column.isHidden"
                                    [width]="column.width!"
                                    [formatter]="column.formatter">
                                    <ng-template igxCell let-cell="cell">{{ cell.value | slNumber: 2 }} %</ng-template>
                                </igx-column>
                            }
                            @case (NumberType.Default) {
                                <igx-column
                                    field="{{ column.fieldName }}"
                                    [filterable]="column.allowFiltering"
                                    [sortable]="column.isSortable"
                                    [header]="column.header"
                                    [dataType]="column.dataType"
                                    [resizable]="column.isResizable"
                                    [disableHiding]="!column.canHide"
                                    [pinned]="column.isPinned"
                                    [hidden]="column.isHidden"
                                    [width]="column.width!"
                                    [formatter]="column.formatter">
                                    <ng-template igxCell let-cell="cell">
                                        {{ cell.value | slNumber }}
                                    </ng-template>
                                </igx-column>
                            }
                        }
                    }
                }
                @case (DataType.Date) {
                    <igx-column
                        field="{{ column.fieldName }}"
                        [filterable]="column.allowFiltering"
                        [sortable]="column.isSortable"
                        [header]="column.header"
                        [dataType]="column.dataType"
                        [resizable]="column.isResizable"
                        [disableHiding]="!column.canHide"
                        [pinned]="column.isPinned"
                        [hidden]="column.isHidden"
                        [width]="column.width!">
                        <ng-template igxCell let-cell="cell">
                            {{ cell.value | localizedDate: getAsDateColumn(column).format }}
                        </ng-template>
                    </igx-column>
                }
                @case (DataType.Boolean) {
                    <igx-column
                        field="{{ column.fieldName }}"
                        [filterable]="column.allowFiltering"
                        [sortable]="column.isSortable"
                        [header]="column.header"
                        [dataType]="column.dataType"
                        [resizable]="column.isResizable"
                        [disableHiding]="!column.canHide"
                        [pinned]="column.isPinned"
                        [hidden]="column.isHidden"
                        [width]="column.width!">
                        @if (getAsBooleanColumn(column).isIcon) {
                            <ng-template igxCell let-cell="cell">
                                <mat-icon>
                                    {{ cell.value ? getAsBooleanColumn(column).trueText : getAsBooleanColumn(column).falseText }}
                                </mat-icon>
                            </ng-template>
                        } @else {
                            <ng-template igxCell let-cell="cell">
                                {{ (cell.value ? getAsBooleanColumn(column).trueText : getAsBooleanColumn(column).falseText) | translate }}
                            </ng-template>
                        }
                    </igx-column>
                }
            }
        }
    }
</igx-grid>
